import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createChart } from './charts/factory';
import ChartService from '../services/ChartService';
import { THEMES } from '../services/themeContext';

/**
 * @typedef {Object} InlineChartData
 * @property {string} chart_type - One of the supported chart types
 * @property {Array<Object>} data - Data-point objects
 * @property {string} [title] - Chart title
 * @property {Object} [options] - Display options
 * @property {string} [chart_id] - Stored chart spec ID
 */

/**
 * @typedef {Object} IdOnlyChartData
 * @property {string} chart_id - Stored chart spec ID to fetch
 */

/**
 * VChartWrapper component — renders validated chart specs.
 * Supports inline specs (chart_type + data) and ID-only references (chart_id).
 * @param {Object} props
 * @param {InlineChartData|IdOnlyChartData} props.chartData - Chart data (inline or ID-only)
 * @param {string} [props.token] - Bearer token for fetch-by-ID requests
 * @param {number|string} [props.minHeight] - Minimum chart container height
 * @param {string} [props.className] - Extra container classes
 */
const VChartWrapper = ({ chartData, token, minHeight = 300, className = '' }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [resolvedSpec, setResolvedSpec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => {
    return document.body.dataset.theme || THEMES.LIGHT;
  });
  const resolvedMinHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          const newTheme = document.body.dataset.theme || THEMES.LIGHT;
          setCurrentTheme(newTheme);
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Memoize the chartData to prevent unnecessary re-renders
  const memoizedChartData = useMemo(() => {
    if (!chartData) return null;
    try {
      return JSON.parse(JSON.stringify(chartData));
    } catch (err) {
      console.error('[VChartWrapper] Error memoizing chart data:', err);
      return chartData;
    }
  }, [JSON.stringify(chartData)]);

  // Fetch chart spec by ID if only chart_id is provided (no inline data)
  useEffect(() => {
    if (!memoizedChartData) return;

    // If we already have inline data, no fetch needed — factory.js handles
    // missing chart_type defaulting, so pass through as-is.
    if (Array.isArray(memoizedChartData.data)) {
      setResolvedSpec(memoizedChartData);
      return;
    }

    // If we have a chart_id but no data, fetch from API
    if (memoizedChartData.chart_id) {
      let cancelled = false;
      setLoading(true);
      setError(null);
      // Resolve token at fetch time — supports both static strings and
      // getter functions so callers can pass getAccessToken() to avoid stale tokens.
      const tokenPromise = typeof token === 'function' ? token() : Promise.resolve(token);
      tokenPromise.then(resolvedToken =>
        ChartService.fetchChartSpec(memoizedChartData.chart_id, resolvedToken)
      )
        .then(spec => { if (!cancelled) setResolvedSpec(spec); })
        .catch(err => {
          if (cancelled) return;
          console.error('[VChartWrapper] Failed to fetch chart spec:', err);
          setError(`Failed to load chart: ${err.message}`);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }

    // Unknown shape
    setError('Invalid chart data: requires data array, or chart_id');
  }, [memoizedChartData, token]);

  // Render the chart when we have a resolved spec.
  // React runs the cleanup function from the previous render before executing
  // the next effect, so we rely solely on the returned cleanup — no manual
  // release at the top (which would cause a double-release).
  useEffect(() => {
    if (!resolvedSpec || !containerRef.current) return;

    try {
      const isDarkMode = currentTheme === THEMES.DARK;
      const newChart = createChart(resolvedSpec, containerRef.current, { isDarkMode });
      setError(null);

      return () => {
        newChart.release();
      };
    } catch (err) {
      console.error('[VChartWrapper] Error rendering chart:', err);
      setError(`Failed to render chart: ${err.message}`);
    }
  }, [resolvedSpec, currentTheme]);

  // Loading state
  if (loading) {
    return (
      <div className="my-4 flex items-center text-sm text-gray-500">
        <span className="mr-2">Loading chart...</span>
      </div>
    );
  }

  // Error state with table fallback
  if (error) {
    const rawData = resolvedSpec?.data || memoizedChartData?.data;
    if (Array.isArray(rawData) && rawData.length > 0) {
      const headers = Object.keys(rawData[0]);
      return (
        <div className="my-4 overflow-x-auto">
          <p className="text-sm text-gray-500 mb-2">Chart could not render. Showing data as table:</p>
          <table className="min-w-full text-sm border border-gray-300">
            <thead>
              <tr>
                {headers.map(h => (
                  <th key={h} className="px-3 py-1 border-b bg-gray-100 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rawData.map((row, i) => (
                <tr key={i}>
                  {headers.map(h => (
                    <td key={h} className="px-3 py-1 border-b">{String(row[h] ?? '')}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded-lg relative my-4" role="alert">
        <div className="flex items-start">
          <span className="text-xl mr-3">&#10060;</span>
          <div>
            <strong className="font-bold">Chart Rendering Failed</strong>
            <p className="text-sm mt-1">The chart could not be rendered. Please check the data format and try again.</p>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-2">
                <summary className="text-xs cursor-pointer text-red-600">Technical Details</summary>
                <pre className="text-xs mt-1 p-2 bg-red-100 rounded overflow-auto">{error}</pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render chart container
  return (
    <div
      ref={containerRef}
      className={`w-full h-full ${className}`.trim()}
      style={{ minHeight: resolvedMinHeight }}
    />
  );
};

export default React.memo(VChartWrapper);
